// /customers/new — New Customer (PAGES.md §3.31)
//
// Manual create flow. Honours CLAUDE.md form rules: strict E.164 phone,
// optional email, optional GSTIN, loyalty enrollment toggle. Surfaces backend
// CRM_PHONE_DUPLICATE / CRM_EMAIL_DUPLICATE as inline field errors.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer, ErrorBoundary, Input, TextArea, Toggle, Button, Alert, GstinInput, DatePicker, useToast } from '@bbpos/ui';
import { ApiError } from '@/lib/api-client';
import {
  useCreateCustomer,
  isValidE164,
  isValidEmail,
  toE164,
} from '@/features/customers';
import type { CustomerCreateInput } from '@/features/customers';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { useTenantDial } from '@/hooks/use-tenant-dial';
import { useTaxRegime } from '@/hooks/use-tax-regime';

type CustomerType = 'INDIVIDUAL' | 'CORPORATE';

interface FormState {
  customer_type: CustomerType;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  date_of_birth: Date | undefined;
  anniversary: Date | undefined;
  address: string;
  city: string;
  state: string;
  pincode: string;
  gstin: string;
  is_loyalty_enrolled: boolean;
  /** CU4-003: opted in by default (decision C9); off = no marketing on any channel. */
  marketing: boolean;
  notes: string;
  tags: string[];
  tagInput: string;
  // Corporate-only company details
  company_name: string;
  company_address: string;
  contact_person: string;
  contact_email: string;
  contact_phone: string;
}

const initial: FormState = {
  customer_type: 'INDIVIDUAL',
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  date_of_birth: undefined,
  anniversary: undefined,
  address: '',
  city: '',
  state: '',
  pincode: '',
  gstin: '',
  is_loyalty_enrolled: true,
  marketing: true,
  notes: '',
  tags: [],
  tagInput: '',
  company_name: '',
  company_address: '',
  contact_person: '',
  contact_email: '',
  contact_phone: '',
};

type FieldError = Partial<Record<keyof FormState, string>>;

function CustomerCreateContent() {
  const dial = useTenantDial();
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  // Statutory tax-id vocabulary/validation derives from the immutable currency.
  // INR → GST_INDIA: label `GSTIN`, GSTIN regex (byte-identical to before).
  // GCC → `TRN`/`VATIN`/… + 15-digit regex; Qatar/Kuwait have no statutory id
  // (`taxIdLabel === null`) ⇒ `GstinInput` renders no label / skips the format check.
  const tax = useTaxRegime();
  const [form, setForm] = useState<FormState>(initial);
  const [errors, setErrors] = useState<FieldError>({});
  const create = useCreateCustomer();

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function addTag() {
    const tag = form.tagInput.trim();
    if (tag && !form.tags.includes(tag) && form.tags.length < 10) {
      setForm((f) => ({ ...f, tags: [...f.tags, tag], tagInput: '' }));
    }
  }

  function stripHtml(s: string): string {
    return s.replace(/<[^>]*>/g, '');
  }

  function toYMD(d: Date | undefined): string | undefined {
    if (!d) return undefined;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  function validate(): { ok: boolean; normalised?: CustomerCreateInput } {
    const errs: FieldError = {};
    const isCorporate = form.customer_type === 'CORPORATE';
    // For corporate customers the identity is the company (validated below),
    // so the person First Name isn't required.
    const trimmedName = stripHtml(form.first_name.trim());
    if (!isCorporate) {
      if (!trimmedName) errs.first_name = 'First name is required';
      else if (trimmedName.length < 2) errs.first_name = 'Name must be at least 2 characters';
      else if (trimmedName.length > 200) errs.first_name = 'Name must be at most 200 characters';
    }

    const canonicalPhone = toE164(form.phone);
    if (!canonicalPhone || !isValidE164(canonicalPhone)) {
      errs.phone = 'Enter a valid phone number';
    }

    if (form.email && !isValidEmail(form.email)) errs.email = 'Invalid email address';
    if (form.gstin && tax.taxIdRegex && !tax.taxIdRegex.test(form.gstin.trim().toUpperCase())) errs.gstin = `Invalid ${tax.taxIdLabel ?? 'tax ID'} format`;
    if (form.address.length > 500) errs.address = 'Address must be at most 500 characters';
    if (form.notes.length > 1000) errs.notes = 'Notes must be at most 1000 characters';
    if (form.tags.length > 10) errs.tags = 'Maximum 10 tags allowed';

    if (isCorporate) {
      const companyName = stripHtml(form.company_name.trim());
      if (!companyName) errs.company_name = 'Company name is required for corporate customers';
      else if (companyName.length > 200) errs.company_name = 'Company name must be at most 200 characters';
      if (form.company_address.length > 500) errs.company_address = 'Company address must be at most 500 characters';
      if (form.contact_person.length > 200) errs.contact_person = 'Contact person must be at most 200 characters';
      if (form.contact_email && !isValidEmail(form.contact_email)) errs.contact_email = 'Invalid contact email address';
      if (form.contact_phone) {
        const canonicalContact = toE164(form.contact_phone);
        if (!canonicalContact || !isValidE164(canonicalContact)) {
          errs.contact_phone = 'Enter a valid contact phone number';
        }
      }
    }

    setErrors(errs);
    if (Object.keys(errs).length > 0) return { ok: false };

    return {
      ok: true,
      normalised: {
        // Corporate accounts have no person name — use the company name as the
        // record's display name, and skip the individual-only person fields.
        first_name: isCorporate ? stripHtml(form.company_name.trim()) : trimmedName,
        last_name: isCorporate ? undefined : form.last_name.trim() || undefined,
        phone: canonicalPhone!,
        email: form.email.trim() || undefined,
        date_of_birth: isCorporate ? undefined : toYMD(form.date_of_birth),
        anniversary: isCorporate ? undefined : toYMD(form.anniversary),
        // Individual address only applies to INDIVIDUAL customers — corporate
        // accounts use the Company Address instead.
        address: isCorporate ? undefined : form.address.trim() || undefined,
        city: isCorporate ? undefined : form.city.trim() || undefined,
        state: isCorporate ? undefined : form.state.trim() || undefined,
        pincode: isCorporate ? undefined : form.pincode.trim() || undefined,
        gstin: form.gstin.trim().toUpperCase() || undefined,
        is_loyalty_enrolled: form.is_loyalty_enrolled,
        marketing_opt_out: form.marketing ? undefined : true,
        notes: form.notes.trim() || undefined,
        tags: form.tags.length > 0 ? form.tags : undefined,
        customer_type: form.customer_type,
        company_name: isCorporate ? stripHtml(form.company_name.trim()) || undefined : undefined,
        company_address: isCorporate ? form.company_address.trim() || undefined : undefined,
        contact_person: isCorporate ? form.contact_person.trim() || undefined : undefined,
        contact_email: isCorporate ? form.contact_email.trim() || undefined : undefined,
        contact_phone: isCorporate ? toE164(form.contact_phone) || undefined : undefined,
      },
    };
  }

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    const result = validate();
    if (!result.ok || !result.normalised) return;
    try {
      const created = await create.mutateAsync(result.normalised);
      toast({ variant: 'success', title: 'Customer created', description: `${created.name} has been added to your CRM.` });
      navigate(`/customers/${created.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        // Map backend duplicate-field errors onto the right input
        const code = (err as unknown as { code?: string }).code;
        if (code === 'CRM_PHONE_DUPLICATE' || /phone/i.test(err.message)) {
          setErrors((prev) => ({ ...prev, phone: 'Phone number already registered' }));
          return;
        }
        if (code === 'CRM_EMAIL_DUPLICATE' || /email/i.test(err.message)) {
          setErrors((prev) => ({ ...prev, email: 'Email already registered' }));
          return;
        }
        toast({ variant: 'error', title: 'Unable to create customer', description: err.message });
        return;
      }
      toast({ variant: 'error', title: 'Unable to create customer', description: 'Please try again.' });
    }
  }

  // Keyboard shortcuts: Ctrl+Enter submit, Esc cancel. We read the latest
  // submit handler through a ref so the listener effect stays
  // dependency-free and we never attach/detach on every keystroke.
  const submitRef = useRef<() => Promise<void>>(() => Promise.resolve());
  submitRef.current = () => handleSubmit();
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        void submitRef.current();
      } else if (e.key === 'Escape') {
        navigate('/customers');
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-fuchsia-500 to-pink-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(168,85,247,0.6)]">
        <div className="pointer-events-none absolute -top-20 -right-10 h-48 w-48 rounded-full bg-white/20 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
            New Profile
          </span>
          <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Add Customer</h1>
          <p className="mt-1.5 text-sm text-white/85 max-w-xl">
            Manually create a customer profile. Most customers are added automatically during billing.
          </p>
        </div>
      </div>

      {/* validate() owns every field message; the browser's native `required`
          check would block the submit first and the app's message never shows. */}
      <form
        noValidate
        onSubmit={handleSubmit}
        className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 p-6 sm:p-7 animate-slide-up"
      >
        {create.isError && !errors.phone && !errors.email ? (
          <div className="mb-5">
            <Alert variant="danger" title="Could not save" description="Check the fields below and try again." />
          </div>
        ) : null}

        {/* Customer Type */}
        <fieldset>
          <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Customer Type</legend>
          <div className="grid grid-cols-2 gap-3 mb-6">
            {([
              { value: 'INDIVIDUAL' as CustomerType, label: 'Individual', hint: 'A single retail customer' },
              { value: 'CORPORATE' as CustomerType, label: 'Corporate', hint: 'A company / B2B account' },
            ]).map((opt) => {
              const active = form.customer_type === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => update('customer_type', opt.value)}
                  className={`rounded-xl border p-4 text-left transition-all duration-200 ${
                    active
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20 ring-2 ring-violet-500/40 shadow-md'
                      : 'border-neutral-200 dark:border-neutral-700 hover:border-violet-300 hover:bg-neutral-50 dark:hover:bg-neutral-700/40'
                  }`}
                  aria-pressed={active}
                >
                  <span className={`block text-sm font-semibold ${active ? 'text-violet-700 dark:text-violet-300' : 'text-neutral-900 dark:text-neutral-100'}`}>{opt.label}</span>
                  <span className="mt-0.5 block text-xs text-neutral-500 dark:text-neutral-400">{opt.hint}</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        {/* Identity — person fields (name, DOB, anniversary) are individual-only;
            corporate accounts keep just the primary Phone/Email. */}
        <fieldset>
          <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">
            {form.customer_type === 'CORPORATE' ? 'Primary Contact' : 'Identity'}
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {form.customer_type === 'INDIVIDUAL' ? (
              <>
                <Input label="First Name" required value={form.first_name} onChange={(v) => update('first_name', v)} error={errors.first_name} />
                <Input label="Last Name" value={form.last_name} onChange={(v) => update('last_name', v)} />
              </>
            ) : null}
            <Input
              label="Phone"
              required
              variant="phone"
              defaultDial={dial}
              placeholder="98765 43210"
              value={form.phone}
              onChange={(v) => update('phone', v)}
              error={errors.phone}
              helperText={errors.phone ? undefined : 'Pick the country code; saved in E.164 format.'}
            />
            <Input label="Email" type="email" placeholder="customer@example.com" value={form.email} onChange={(v) => update('email', v)} error={errors.email} />
            {form.customer_type === 'INDIVIDUAL' ? (
              <>
                <DatePicker label="Date of Birth" variant="single" value={form.date_of_birth} onChange={(v) => update('date_of_birth', v)} maxDate={new Date()} />
                <DatePicker label="Anniversary" variant="single" value={form.anniversary} onChange={(v) => update('anniversary', v)} />
              </>
            ) : null}
          </div>
        </fieldset>

        {/* Company Details — corporate only */}
        {form.customer_type === 'CORPORATE' ? (
          <fieldset className="animate-fade-in">
            <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Company Details</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="sm:col-span-2">
                <Input label="Company Name" required value={form.company_name} onChange={(v) => update('company_name', v)} error={errors.company_name} />
              </div>
              <div className="sm:col-span-2">
                <TextArea label="Company Address" value={form.company_address} onChange={(v) => update('company_address', v)} error={errors.company_address} maxLength={500} rows={2} />
              </div>
              <Input label="Contact Person" value={form.contact_person} onChange={(v) => update('contact_person', v)} error={errors.contact_person} />
              <Input label="Contact Email" type="email" placeholder="accounts@company.com" value={form.contact_email} onChange={(v) => update('contact_email', v)} error={errors.contact_email} />
              <Input
                label="Contact Phone"
                variant="phone"
                defaultDial={dial}
                placeholder="98765 43210"
                value={form.contact_phone}
                onChange={(v) => update('contact_phone', v)}
                error={errors.contact_phone}
                helperText={errors.contact_phone ? undefined : 'Pick the country code; saved in E.164 format.'}
              />
            </div>
          </fieldset>
        ) : null}

        {/* Address — individual customers only; corporate accounts use the
            Company Address above. */}
        {form.customer_type === 'INDIVIDUAL' ? (
          <fieldset className="animate-fade-in">
            <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Address</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="sm:col-span-2">
                <TextArea label="Street Address" value={form.address} onChange={(v) => update('address', v)} error={errors.address} maxLength={500} rows={2} />
              </div>
              <Input label="City" value={form.city} onChange={(v) => update('city', v)} />
              <Input label="State" value={form.state} onChange={(v) => update('state', v)} />
              <Input label="Pincode" value={form.pincode} onChange={(v) => update('pincode', v)} />
            </div>
          </fieldset>
        ) : null}

        {/* Business (GSTIN) */}
        <fieldset>
          <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Business (optional)</legend>
          <div className="mb-6">
            <GstinInput value={form.gstin} onChange={(v) => update('gstin', v)} regime={tax} error={errors.gstin} />
          </div>
        </fieldset>

        {/* Tags */}
        <fieldset>
          <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Tags</legend>
          <div className="mb-6">
            <div className="flex flex-wrap gap-2 mb-2">
              {form.tags.map((tag, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full bg-violet-100 dark:bg-violet-900/30 px-3 py-1 text-xs font-semibold text-violet-700 dark:text-violet-300 ring-1 ring-violet-200 dark:ring-violet-700">
                  {tag}
                  <button type="button" onClick={() => setForm((f) => ({ ...f, tags: f.tags.filter((_, idx) => idx !== i) }))} className="ml-0.5 text-violet-400 hover:text-violet-600 dark:hover:text-violet-200 transition-colors" aria-label={`Remove tag ${tag}`}>&times;</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder={form.tags.length >= 10 ? 'Max 10 tags' : 'Type a tag and press Enter'}
                value={form.tagInput}
                onChange={(v) => setForm((f) => ({ ...f, tagInput: v }))}
                onKeyDown={(e) => {
                  // Enter would otherwise implicitly submit the whole form (CU1-001).
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addTag();
                  }
                }}
                disabled={form.tags.length >= 10}
              />
              <Button
                type="button"
                variant="secondary"
                disabled={!form.tagInput.trim() || form.tags.length >= 10}
                onClick={addTag}
              >
                Add
              </Button>
            </div>
            {errors.tags ? <p className="mt-1 text-xs text-danger-600">{errors.tags}</p> : null}
            <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">{form.tags.length}/10 tags</p>
          </div>
        </fieldset>

        {/* Loyalty */}
        <fieldset>
          <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Loyalty</legend>
          <div className="rounded-xl bg-gradient-to-br from-violet-50 to-pink-50 dark:from-violet-900/20 dark:to-pink-900/20 p-4 mb-6 ring-1 ring-violet-100 dark:ring-violet-800/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Enroll in loyalty program</p>
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  Customer earns 1 point per {getCurrencySymbol(currency)}100 spent. Points can be redeemed at checkout.
                </p>
              </div>
              <Toggle checked={form.is_loyalty_enrolled} onChange={(v) => update('is_loyalty_enrolled', v)} />
            </div>
          </div>
        </fieldset>

        {/* Marketing consent (CU4-003) */}
        <fieldset>
          <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Marketing</legend>
          <div className="rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-900/20 dark:to-violet-900/20 p-4 mb-6 ring-1 ring-indigo-100 dark:ring-indigo-800/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Send marketing messages</p>
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  Campaigns, Deal of the Day and re-order links. Turn off if the customer does not want them. Bills and receipts are always sent.
                </p>
              </div>
              <Toggle checked={form.marketing} onChange={(v) => update('marketing', v)} />
            </div>
          </div>
        </fieldset>

        {/* Notes */}
        <fieldset className="mb-6">
          <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Notes</legend>
          <TextArea placeholder="Internal note (optional)" value={form.notes} onChange={(v) => update('notes', v)} error={errors.notes} maxLength={1000} rows={3} />
        </fieldset>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button variant="ghost" type="button" onClick={() => navigate('/customers')}>Cancel</Button>
          <Button variant="primary" type="submit" loading={create.isPending}>Create Customer</Button>
        </div>
        <p className="mt-3 text-xs text-neutral-400 dark:text-neutral-500 text-right">Press <kbd className="rounded bg-neutral-100 dark:bg-neutral-700 px-1.5 py-0.5 font-mono text-[10px]">Ctrl+Enter</kbd> to submit · <kbd className="rounded bg-neutral-100 dark:bg-neutral-700 px-1.5 py-0.5 font-mono text-[10px]">Esc</kbd> to cancel</p>
      </form>
    </div>
  );
}

export function CustomerCreatePage() {
  return (
    <PageContainer
      title="New Customer"
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Customers', href: '/customers' }, { label: 'New Customer' }]}
    >
      <ErrorBoundary resetKey="customers-new">
        <CustomerCreateContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
